import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar, Clock, Video, Building2, MapPin, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONSULTATION_TYPES = [
  { value: 'Online Appointment', label: 'Online Appointment', icon: Video, description: 'Zoom/Video call consultation' },
  { value: 'Office Appointment', label: 'Office Appointment', icon: Building2, description: 'Meet at our office location' },
  { value: 'Client Location Appointment', label: 'Client Location Appointment', icon: MapPin, description: 'Visit client at their location' },
];

export default function ConsultationWizardModal({ 
  isOpen, 
  onClose, 
  lead, 
  onSuccess 
}) {
  const [consultationType, setConsultationType] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setConsultationType('');
      setAppointmentDate('');
      setAppointmentTime('');
      setNotes('');
      setError('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate time slots (8 AM to 8 PM in 30-min intervals)
  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 20) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  const handleSubmit = async () => {
    // Validation
    if (!consultationType) {
      setError('Please select a consultation type');
      return;
    }
    if (!appointmentDate) {
      setError('Please select an appointment date');
      return;
    }
    if (!appointmentTime) {
      setError('Please select an appointment time');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create appointment and update lead stage in one call
      const startDatetime = `${appointmentDate}T${appointmentTime}:00`;
      
      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}/book-consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          consultation_type: consultationType,
          start_datetime: startDatetime,
          notes: notes || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Consultation booked successfully!');
        onSuccess && onSuccess(data);
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to book consultation');
      }
    } catch (err) {
      logger.error('Error booking consultation:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const minDate = format(new Date(), 'yyyy-MM-dd');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="consultation-wizard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="h-5 w-5 text-blue-600" />
            Book Consultation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Lead Info */}
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-500">Booking consultation for:</p>
            <p className="font-medium text-slate-900">
              {lead?.first_name} {lead?.last_name}
            </p>
            {lead?.email && <p className="text-sm text-slate-500">{lead.email}</p>}
          </div>

          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Consultation Type Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Consultation Type <span className="text-red-500">*</span>
            </Label>
            <div className="grid gap-3">
              {CONSULTATION_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = consultationType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setConsultationType(type.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`consultation-type-${type.value.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date & Time Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="appointment-date" className="text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="appointment-date"
                type="date"
                min={minDate}
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                className="input-goldsands"
                data-testid="consultation-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="appointment-time" className="text-sm font-medium">
                Time <span className="text-red-500">*</span>
              </Label>
              <Select value={appointmentTime} onValueChange={setAppointmentTime}>
                <SelectTrigger data-testid="consultation-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="consultation-notes" className="text-sm font-medium">
              Notes (optional)
            </Label>
            <Textarea
              id="consultation-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about the consultation..."
              className="input-goldsands min-h-[80px]"
              data-testid="consultation-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="btn-accent"
            data-testid="consultation-submit"
          >
            {loading ? 'Booking...' : 'Book Consultation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
